import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { DealProps } from "./use-shopping-deals";

interface UseShoppingDealsPageReturn {
  deals: DealProps[];
  isLoading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  searchQuery: string;
  selectedStores: string[];
  selectedPriceRanges: string[];
  discountSortOrder: "asc" | "desc" | null;
  availableStores: string[];
  priceRanges: { id: string; name: string; min: number; max: number | null }[];
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleStoreChange: (store: string, isChecked: boolean) => void;
  handlePriceRangeChange: (rangeId: string, isChecked: boolean) => void;
  handleDiscountSortChange: (order: "asc" | "desc" | null) => void;
  resetFilters: () => void;
  loadMoreDeals: () => Promise<void>;
}

// Các phạm vi giá
const PRICE_RANGES = [
  { id: "under-50", name: "Under $50", min: 0, max: 50 },
  { id: "50-100", name: "$50 - $100", min: 50, max: 100 },
  { id: "100-200", name: "$100 - $200", min: 100, max: 200 },
  { id: "over-200", name: "Over $200", min: 200, max: null },
];

// Chuyển đổi giá từ string sang number (xóa ký tự $ và dấu phẩy)
const parsePrice = (priceStr: string): number => {
  return parseFloat(priceStr.replace(/[$,]/g, ""));
};

export const useShoppingDealsPage = (): UseShoppingDealsPageReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState<DealProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedPriceRanges, setSelectedPriceRanges] = useState<string[]>([]);
  const [discountSortOrder, setDiscountSortOrder] = useState<
    "asc" | "desc" | null
  >(null);
  const [availableStores, setAvailableStores] = useState<string[]>([]);
  const [totalDeals, setTotalDeals] = useState(0);

  const itemsPerPage = 8;

  // Đồng bộ URL params
  useEffect(() => {
    const storeParam = searchParams.get("store") || "";
    const priceParam = searchParams.get("price") || "";
    const searchParam = searchParams.get("q") || "";
    const sortParam = searchParams.get("sort") || "";

    if (storeParam) {
      setSelectedStores(storeParam.split(","));
    }

    if (priceParam) {
      setSelectedPriceRanges(priceParam.split(","));
    }

    if (searchParam) {
      setSearchQuery(searchParam);
    }

    if (sortParam === "discount_asc") {
      setDiscountSortOrder("asc");
    } else if (sortParam === "discount_desc") {
      setDiscountSortOrder("desc");
    } else {
      setDiscountSortOrder(null);
    }
  }, [searchParams]);

  // Hàm lấy danh sách cửa hàng
  const fetchStores = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("deals")
        .select("store")
        .eq("active", true);

      if (error) throw error;

      if (data && data.length > 0) {
        // Lọc các cửa hàng duy nhất
        const uniqueStores = Array.from(
          new Set(data.map((item) => item.store))
        );
        setAvailableStores(uniqueStores);
      }
    } catch (error) {
      console.error("Lỗi khi tải danh sách cửa hàng:", error);
    }
  }, []);

  // Tải dữ liệu ban đầu
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Áp dụng bộ lọc và tải dữ liệu
  useEffect(() => {
    const applyFilters = async () => {
      setIsLoading(true);
      setDeals([]);
      setPage(1);

      try {
        // Lấy tất cả deals để có thể lọc
        const { data, error } = await supabase
          .from("deals")
          .select("*")
          .eq("active", true);

        if (error) throw error;

        if (data && data.length > 0) {
          // Lọc dữ liệu theo bộ lọc
          let filteredDeals = data;

          // Lọc theo cửa hàng
          if (selectedStores.length > 0) {
            filteredDeals = filteredDeals.filter((deal) =>
              selectedStores.includes(deal.store)
            );
          }

          // Lọc theo phạm vi giá
          if (selectedPriceRanges.length > 0) {
            filteredDeals = filteredDeals.filter((deal) => {
              const price = parsePrice(deal.sale_price);
              return selectedPriceRanges.some((rangeId) => {
                const range = PRICE_RANGES.find((r) => r.id === rangeId);
                if (!range) return false;

                if (range.max === null) {
                  return price >= range.min;
                }
                return price >= range.min && price <= range.max;
              });
            });
          }

          // Lọc theo từ khóa tìm kiếm
          if (searchQuery.trim() !== "") {
            const query = searchQuery.toLowerCase();
            filteredDeals = filteredDeals.filter(
              (deal) =>
                deal.title.toLowerCase().includes(query) ||
                deal.store.toLowerCase().includes(query)
            );
          }

          // Sắp xếp theo giảm giá
          if (discountSortOrder) {
            filteredDeals.sort((a, b) => {
              const discountA = parseFloat(a.discount.replace("%", ""));
              const discountB = parseFloat(b.discount.replace("%", ""));

              return discountSortOrder === "asc"
                ? discountA - discountB
                : discountB - discountA;
            });
          }

          // Lưu tổng số deals sau khi lọc
          setTotalDeals(filteredDeals.length);

          // Chỉ lấy số lượng deals cho trang đầu tiên
          const paginatedDeals = filteredDeals.slice(0, itemsPerPage);

          // Format dữ liệu với delay animation
          const dealsWithDelay = paginatedDeals.map((deal, index) => ({
            id: deal.id,
            title: deal.title,
            image: deal.image,
            originalPrice: deal.original_price,
            salePrice: deal.sale_price,
            discount: deal.discount,
            store: deal.store,
            link: deal.link,
            delay: index * 0.1,
          }));

          setDeals(dealsWithDelay);
          setHasMore(filteredDeals.length > itemsPerPage);
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu deals:", error);
        setError("Không thể tải danh sách deal. Vui lòng thử lại sau.");
      } finally {
        setIsLoading(false);
      }
    };

    applyFilters();
  }, [searchQuery, selectedStores, selectedPriceRanges, discountSortOrder]);

  // Tải thêm deals
  const loadMoreDeals = async () => {
    if (!hasMore || loadingMore) return;

    setLoadingMore(true);

    try {
      // Lấy tất cả deals để có thể lọc
      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .eq("active", true);

      if (error) throw error;

      if (data && data.length > 0) {
        // Lọc dữ liệu theo bộ lọc
        let filteredDeals = data;

        // Lọc theo cửa hàng
        if (selectedStores.length > 0) {
          filteredDeals = filteredDeals.filter((deal) =>
            selectedStores.includes(deal.store)
          );
        }

        // Lọc theo phạm vi giá
        if (selectedPriceRanges.length > 0) {
          filteredDeals = filteredDeals.filter((deal) => {
            const price = parsePrice(deal.sale_price);
            return selectedPriceRanges.some((rangeId) => {
              const range = PRICE_RANGES.find((r) => r.id === rangeId);
              if (!range) return false;

              if (range.max === null) {
                return price >= range.min;
              }
              return price >= range.min && price <= range.max;
            });
          });
        }

        // Lọc theo từ khóa tìm kiếm
        if (searchQuery.trim() !== "") {
          const query = searchQuery.toLowerCase();
          filteredDeals = filteredDeals.filter(
            (deal) =>
              deal.title.toLowerCase().includes(query) ||
              deal.store.toLowerCase().includes(query)
          );
        }

        // Sắp xếp theo giảm giá
        if (discountSortOrder) {
          filteredDeals.sort((a, b) => {
            const discountA = parseFloat(a.discount.replace("%", ""));
            const discountB = parseFloat(b.discount.replace("%", ""));

            return discountSortOrder === "asc"
              ? discountA - discountB
              : discountB - discountA;
          });
        }

        // Tính toán phân trang
        const nextPage = page + 1;
        const startIndex = (nextPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;

        // Kiểm tra nếu đã hiển thị tất cả deals
        if (startIndex >= filteredDeals.length) {
          setHasMore(false);
          setLoadingMore(false);
          return;
        }

        // Lấy deals cho trang tiếp theo
        const nextPageDeals = filteredDeals.slice(startIndex, endIndex);

        // Format dữ liệu với delay animation
        const dealsWithDelay = nextPageDeals.map((deal, index) => ({
          id: deal.id,
          title: deal.title,
          image: deal.image,
          originalPrice: deal.original_price,
          salePrice: deal.sale_price,
          discount: deal.discount,
          store: deal.store,
          link: deal.link,
          delay: index * 0.1,
        }));

        // Thêm vào danh sách hiện tại
        setDeals((prevDeals) => [...prevDeals, ...dealsWithDelay]);
        setPage(nextPage);
        setHasMore(endIndex < filteredDeals.length);
      }
    } catch (error) {
      console.error("Lỗi khi tải thêm deals:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Xử lý thay đổi tìm kiếm
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    updateSearchParams("q", e.target.value);
  };

  // Xử lý thay đổi lọc cửa hàng
  const handleStoreChange = (store: string, isChecked: boolean) => {
    let newSelectedStores;

    if (isChecked) {
      newSelectedStores = [...selectedStores, store];
    } else {
      newSelectedStores = selectedStores.filter((s) => s !== store);
    }

    setSelectedStores(newSelectedStores);
    updateSearchParams("store", newSelectedStores.join(","));
  };

  // Xử lý thay đổi phạm vi giá
  const handlePriceRangeChange = (rangeId: string, isChecked: boolean) => {
    let newSelectedPriceRanges;

    if (isChecked) {
      newSelectedPriceRanges = [...selectedPriceRanges, rangeId];
    } else {
      newSelectedPriceRanges = selectedPriceRanges.filter(
        (id) => id !== rangeId
      );
    }

    setSelectedPriceRanges(newSelectedPriceRanges);
    updateSearchParams("price", newSelectedPriceRanges.join(","));
  };

  // Xử lý thay đổi sắp xếp theo giảm giá
  const handleDiscountSortChange = (order: "asc" | "desc" | null) => {
    setDiscountSortOrder(order);

    if (order === "asc") {
      updateSearchParams("sort", "discount_asc");
    } else if (order === "desc") {
      updateSearchParams("sort", "discount_desc");
    } else {
      updateSearchParams("sort", "");
    }
  };

  // Cập nhật URL params
  const updateSearchParams = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }

    setSearchParams(params);
  };

  // Reset tất cả bộ lọc
  const resetFilters = () => {
    setSelectedStores([]);
    setSelectedPriceRanges([]);
    setSearchQuery("");
    setDiscountSortOrder(null);
    setSearchParams({});
  };

  return {
    deals,
    isLoading,
    loadingMore,
    error,
    hasMore,
    searchQuery,
    selectedStores,
    selectedPriceRanges,
    discountSortOrder,
    availableStores,
    priceRanges: PRICE_RANGES,
    handleSearchChange,
    handleStoreChange,
    handlePriceRangeChange,
    handleDiscountSortChange,
    resetFilters,
    loadMoreDeals,
  };
};
